import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { Check, ArrowRight, ArrowUpRight } from 'lucide-react';
const plans = [{
  name: 'Team',
  price: '$799',
  cadence: '/month',
  note: '$7,990 annual · billed yearly',
  desc: 'Recurring validation for startups and small security teams.',
  features: ['Continuous attack-path validation', 'Cloud, identity, SaaS, code, external coverage', 'Remediation guidance', 'Basic fix verification', 'Email & Slack notifications', 'Monthly evidence summary'],
  cta: 'Join Team Beta'
}, {
  name: 'Business',
  price: '$1,999',
  cadence: '/month',
  note: '$19,990 annual · most teams start here',
  desc: 'For companies under customer, compliance, or AI security pressure.',
  features: ['Everything in Team', 'AI app validation', 'Full fix verification history', 'Customer-ready evidence packs', 'Cyber-insurance & SOC 2 readiness', 'Jira integration', 'Quarterly executive summary'],
  cta: 'Join Business Beta',
  highlight: true
}, {
  name: 'Scale',
  price: '$4,999',
  cadence: '/month',
  note: '$49,990 annual · for larger orgs',
  desc: 'For distributed teams, business units, and AI product teams.',
  features: ['Everything in Business', 'Optional internal runner', 'Multiple teams / business units', 'SSO, RBAC, audit logs', 'Expanded AI app validation', 'API access'],
  cta: 'Talk to us'
}];
const advanced = [{
  name: 'Snapshot',
  price: '$299',
  desc: 'One-time attack-path report. Free for select early access teams.',
  cta: 'Request Snapshot'
}, {
  name: 'MSSP',
  price: '$2,500/mo + per-client',
  desc: 'Multi-tenant dashboard, white-label evidence, bulk onboarding.',
  cta: 'Partner with us'
}, {
  name: 'Enterprise',
  price: 'Custom',
  desc: 'Custom scope, dedicated runners, procurement support.',
  cta: 'Contact sales'
}];
export function Pricing() {
  return <section id="pricing" className="relative w-full py-32 border-t border-white/5" data-id="element-687">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-688">
        <SectionLabel number="17" label="Pricing" className="mb-8" data-id="element-689" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-690">
          <div className="lg:col-span-8" data-id="element-691">
            <SectionHeading size="xl" data-id="element-692">
              Enterprise outcome.
              <br data-id="element-693" />
              <span className="text-white/40" data-id="element-694">SaaS packaging.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-4 lg:pt-4" data-id="element-695">
            <p className="text-base text-white/60 leading-relaxed" data-id="element-696">
              Transparent, usage-aware pricing from first report to continuous
              validation. Final pricing may evolve during beta.
            </p>
          </div>
        </div>

        {/* Three primary plans */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-16" data-id="element-697">
          {plans.map((p, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className={`relative p-8 rounded-2xl border flex flex-col transition-colors ${p.highlight ? 'bg-gradient-to-br from-[#1a8fd4]/10 to-[#0a0d12] border-[#1a8fd4]/40' : 'bg-[#070a0e] border-white/[0.06] hover:border-white/15'}`} data-id="element-698">
              {p.highlight && <div className="absolute -top-3 left-8 px-3 py-1 bg-[#1a8fd4] text-white text-[10px] font-mono uppercase tracking-widest rounded-full" data-id="element-699">
                  Most Popular
                </div>}

              {/* Header */}
              <div className="mb-8" data-id="element-700">
                <h3 className="text-2xl font-semibold text-white mb-2 tracking-tight" data-id="element-701">
                  {p.name}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed" data-id="element-702">
                  {p.desc}
                </p>
              </div>

              {/* Price */}
              <div className="mb-8 pb-8 border-b border-white/5" data-id="element-703">
                <div className="flex items-baseline gap-1" data-id="element-704">
                  <span className="text-4xl font-semibold text-white tracking-tight" data-id="element-705">
                    {p.price}
                  </span>
                  {p.cadence && <span className="text-sm text-white/40" data-id="element-706">{p.cadence}</span>}
                </div>
                {p.note && <p className="font-mono text-xs text-[#1a8fd4]/80 mt-2" data-id="element-707">
                    {p.note}
                  </p>}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-1" data-id="element-708">
                {p.features.map((f, j) => <li key={j} className="flex items-start gap-3" data-id="element-709">
                    <Check className="w-4 h-4 text-[#1a8fd4] flex-shrink-0 mt-0.5" data-id="element-710" />
                    <span className="text-sm text-white/75 leading-relaxed" data-id="element-711">
                      {f}
                    </span>
                  </li>)}
              </ul>

              {/* CTA */}
              <a href="#early-access" className={`group w-full flex items-center justify-between gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors ${p.highlight ? 'bg-white text-black hover:bg-white/90' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`} data-id="element-712">
                <span data-id="element-713">{p.cta}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-714" />
              </a>
            </motion.div>)}
        </div>

        {/* Advanced options — compact callout instead of separate plans */}
        <div className="mb-12" data-id="element-715">
          <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-6" data-id="element-716">
            Looking for something else?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-717">
            {advanced.map((a, i) => <motion.a key={i} href="#early-access" initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i * 0.05
          }} className="group p-6 rounded-xl bg-[#070a0e] border border-white/[0.06] hover:border-[#1a8fd4]/30 transition-colors" data-id="element-718">
                <div className="flex items-start justify-between gap-4 mb-3" data-id="element-719">
                  <h4 className="text-base font-semibold text-white" data-id="element-720">
                    {a.name}
                  </h4>
                  <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#1a8fd4] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" data-id="element-721" />
                </div>
                <p className="text-sm text-white/50 leading-relaxed mb-3" data-id="element-722">
                  {a.desc}
                </p>
                <p className="font-mono text-xs text-[#1a8fd4]" data-id="element-723">{a.price}</p>
              </motion.a>)}
          </div>
        </div>

        {/* Philosophy — kept simple */}
        <div className="rounded-2xl bg-[#070a0e] border border-white/5 p-8 md:p-12" data-id="element-724">
          <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-725">
            Pricing Philosophy
          </p>
          <p className="text-xl md:text-2xl text-white leading-relaxed max-w-3xl tracking-tight" data-id="element-726">
            Continuous validation should not require a six-figure platform, a
            long procurement cycle, or a dedicated red team.
          </p>
        </div>
      </div>
    </section>;
}